import { Form, Field } from 'react-final-form';
import styled from 'styled-components';

import { BaseModal } from '../../../../../components/Toolkit/Modal/BaseModal';
import type { ModalRequiredProps } from '../../../../../components/Toolkit/Modal/BaseModal';
import { InfoTitle } from '../../../../../components/Toolkit/InfoTitle/InfoTitle';
import { TextField } from '../../../../../components/Toolkit/Inputs/TextField';
import { Button } from '../../../../../components/Toolkit/Button/Button';
import { Link } from '../../../../../components/Toolkit/Button/Link';
import {
  required,
  isPhoneNumber,
  composeValidators,
} from '../../../../../helpers/InputValidators';
import { handleErrorMessage } from '../../../../../helpers/forms';

export interface StripeCaptureBuyerPhoneNumberModalProps
  extends ModalRequiredProps {
  adId: number;
  stripeQuantity: number;
  cdnUrl: string;
  handleCallStripeCheckout: (
    adId: number,
    quantity: number,
    phoneNumber?: string,
  ) => Promise<void>;
}

const STextField = styled(TextField)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const TextContent = styled.p`
  ${({ theme }) => theme.fontSize.B14}
  margin-bottom: ${({ theme }) => theme.spacing.M24};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const Disclaimer = styled.p`
  ${({ theme }) => theme.fontSize.B10}
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  text-align: center;
`;

const SInfoTitle = styled(InfoTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

function StripeCaptureBuyerPhoneNumberModal({
  adId,
  stripeQuantity,
  isOpen,
  cdnUrl,
  askToClose,
  handleCallStripeCheckout,
}: StripeCaptureBuyerPhoneNumberModalProps) {
  async function handleSubmit({ phoneNumber }: { phoneNumber: string }) {
    try {
      await handleCallStripeCheckout(adId, stripeQuantity, phoneNumber);
    } catch (error) {
      return error;
    }
  }

  function handleClose() {
    askToClose();
  }

  return (
    <BaseModal isOpen={isOpen} askToClose={handleClose} testId="stripe-modal">
      <SInfoTitle
        title="Enter phone number"
        illustration={`${cdnUrl}/images/logos/donedeal-letter-logo.svg`}
      />
      <TextContent>
        A phone number is required to adequately support the fulfilment of your
        purchase.
      </TextContent>
      <Form
        onSubmit={handleSubmit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="phoneNumber"
              validate={composeValidators(
                required('Phone Number Required'),
                isPhoneNumber,
              )}
            >
              {({ input, meta }) => (
                <STextField
                  type="tel"
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  label="Phone Number"
                  placeholder="e.g. 0841234567"
                  errorMessage={handleErrorMessage(meta)}
                  data-testid="stripe-phone-number-input"
                />
              )}
            </Field>
            <Disclaimer>
              By selecting Proceed to checkout below, I agree to the DoneDeal{' '}
              <Link
                href="https://hello.donedeal.ie/hc/en-us/articles/201251761-Terms-Conditions-of-Use-of-DoneDeal"
                target="_blank"
                rel="noopener noreferrer"
                ofType="SECONDARY"
                inline
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                href="https://hello.donedeal.ie/hc/en-us/articles/201251721-Privacy-Policy"
                target="_blank"
                rel="noopener noreferrer"
                ofType="SECONDARY"
                inline
              >
                Privacy Policy
              </Link>
            </Disclaimer>
            <Button type="submit" data-testid="proceed-to-checkout">
              Proceed to checkout
            </Button>
          </form>
        )}
      />
    </BaseModal>
  );
}

export { StripeCaptureBuyerPhoneNumberModal };
